import React, {
  useState,
  useEffect,
  useReducer,
  useCallback,
  useContext,
} from "react";
import { toast } from "react-toastify";

import { makeStyles, useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import IconButton from "@material-ui/core/IconButton";
import SearchIcon from "@material-ui/icons/Search";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";
import Tooltip from "@material-ui/core/Tooltip";

import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import LabelIcon from "@material-ui/icons/Label";
import ViewWeekIcon from "@material-ui/icons/ViewWeek";
import SwapVertIcon from "@material-ui/icons/SwapVert";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import TagModal from "../../components/TagModal";
import KanbanOrderModal from "../../components/KanbanOrderModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import MobileRecordCard, { MobileRecordCardSkeleton } from "../../components/MobileRecordCard";
import toastError from "../../errors/toastError";
import { Chip } from "@material-ui/core";
import { socketConnection } from "../../services/socket";
import { AuthContext } from "../../context/Auth/AuthContext";

const reducer = (state, action) => {
  if (action.type === "LOAD_TAGS") {
    const tags = action.payload;
    const newTags = [];

    tags.forEach((tag) => {
      const tagIndex = state.findIndex((s) => s.id === tag.id);
      if (tagIndex !== -1) {
        state[tagIndex] = tag;
      } else {
        newTags.push(tag);
      }
    });

    return [...state, ...newTags];
  }

  if (action.type === "UPDATE_TAGS") {
    const tag = action.payload;
    const tagIndex = state.findIndex((s) => s.id === tag.id);

    if (tagIndex !== -1) {
      state[tagIndex] = tag;
      return [...state];
    } else {
      return [tag, ...state];
    }
  }

  if (action.type === "DELETE_TAG") {
    const tagId = action.payload;

    const tagIndex = state.findIndex((s) => s.id === tagId);
    if (tagIndex !== -1) {
      state.splice(tagIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  tagChip: {
    backgroundColor: "transparent",
    color: "#fff",
    fontWeight: 600,
    fontSize: "0.72rem",
    borderRadius: 12,
    height: 22,
    letterSpacing: "0.03em",
  },
  kanbanChip: {
    backgroundColor: "#7b1fa2",
    color: "#fff",
    fontWeight: 600,
    fontSize: "0.65rem",
    borderRadius: 8,
    height: 20,
  },
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(6, 2),
    color: theme.palette.text.disabled,
  },
  emptyIcon: {
    fontSize: 56,
    marginBottom: theme.spacing(1.5),
    opacity: 0.35,
  },
}));

const Tags = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const { user } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedTag, setSelectedTag] = useState(null);
  const [deletingTag, setDeletingTag] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [tags, dispatch] = useReducer(reducer, []);
  const [tagModalOpen, setTagModalOpen] = useState(false);
  const [kanbanOrderModalOpen, setKanbanOrderModalOpen] = useState(false);

  const fetchTags = useCallback(async () => {
    try {
      const { data } = await api.get("/tags/", {
        params: { searchParam, pageNumber },
      });
      dispatch({ type: "LOAD_TAGS", payload: data.tags });
      setHasMore(data.hasMore);
      setLoading(false);
    } catch (err) {
      toastError(err);
    }
  }, [searchParam, pageNumber]);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      fetchTags();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber, fetchTags]);

  useEffect(() => {
    const socket = socketConnection({ companyId: user.companyId });

    socket.on("user", (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_TAGS", payload: data.tags });
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_TAG", payload: +data.tagId });
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [user]);

  // A reordenação das raias grava em todas as tags Kanban da empresa, por
  // isso o endpoint é restrito a admin — o botão acompanha essa regra.
  const isAdmin = user.profile === "admin";

  const handleOpenTagModal = () => {
    setSelectedTag(null);
    setTagModalOpen(true);
  };

  const handleCloseTagModal = () => {
    setSelectedTag(null);
    setTagModalOpen(false);
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleEditTag = (tag) => {
    setSelectedTag(tag);
    setTagModalOpen(true);
  };

  const handleDeleteTag = async (tagId) => {
    try {
      await api.delete(`/tags/${tagId}`);
      toast.success(i18n.t("tags.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingTag(null);
    setSearchParam("");
    setPageNumber(1);

    dispatch({ type: "RESET" });
    setPageNumber(1);
    await fetchTags();
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const renderActions = (tag) => (
    <>
      <Tooltip title="Editar">
        <IconButton size="small" onClick={() => handleEditTag(tag)}>
          <EditIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Excluir">
        <IconButton
          size="small"
          onClick={() => {
            setConfirmModalOpen(true);
            setDeletingTag(tag);
          }}
        >
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </>
  );

  const emptyState = (
    <Box className={classes.emptyState}>
      <LabelIcon className={classes.emptyIcon} />
      <Typography variant="body2" gutterBottom>
        {searchParam
          ? `Nenhuma tag encontrada para "${searchParam}"`
          : "Nenhuma tag cadastrada ainda."}
      </Typography>
      {!searchParam && (
        <Button
          size="small"
          color="primary"
          variant="outlined"
          onClick={handleOpenTagModal}
          style={{ marginTop: 8 }}
        >
          Criar primeira tag
        </Button>
      )}
    </Box>
  );

  return (
    <MainContainer>
      <ConfirmationModal
        title={deletingTag && `${i18n.t("tags.confirmationModal.deleteTitle")}`}
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={() => handleDeleteTag(deletingTag.id)}
      >
        {i18n.t("tags.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <KanbanOrderModal
        open={kanbanOrderModalOpen}
        onClose={() => setKanbanOrderModalOpen(false)}
        reload={fetchTags}
      />
      <TagModal
        open={tagModalOpen}
        onClose={handleCloseTagModal}
        reload={fetchTags}
        aria-labelledby="form-dialog-title"
        tagId={selectedTag && selectedTag.id}
      />
      <MainHeader>
        <Title>{i18n.t("tags.title")}</Title>
        <MainHeaderButtonsWrapper>
          <TextField
            placeholder="Buscar tag..."
            type="search"
            value={searchParam}
            onChange={handleSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ color: "gray" }} />
                </InputAdornment>
              ),
            }}
          />
          {isAdmin && (
            <Button
              variant="outlined"
              color="primary"
              startIcon={<SwapVertIcon />}
              onClick={() => setKanbanOrderModalOpen(true)}
            >
              Ordenar Kanban
            </Button>
          )}
          <Button
            variant="contained"
            color="primary"
            onClick={handleOpenTagModal}
          >
            {i18n.t("tags.buttons.add")}
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Paper
        className={classes.mainPaper}
        variant="outlined"
        onScroll={handleScroll}
      >
        {!loading && tags.length === 0 && emptyState}

        {isMobile ? (
          (tags.length > 0 || loading) && (
            <div>
              {tags.map((tag) => (
                <MobileRecordCard
                  key={tag.id}
                  title={
                    <Chip
                      className={classes.tagChip}
                      style={{ backgroundColor: tag.color }}
                      label={tag.name}
                      size="small"
                    />
                  }
                  headerAction={renderActions(tag)}
                  rows={[
                    { label: i18n.t("tags.table.tickets"), value: tag.ticketsCount },
                  ]}
                  chips={
                    tag.kanban === 1 && (
                      <Chip
                        className={classes.kanbanChip}
                        icon={<ViewWeekIcon style={{ fontSize: 12, color: "#fff" }} />}
                        label="Kanban"
                        size="small"
                      />
                    )
                  }
                />
              ))}
              {loading &&
                [0, 1, 2].map((i) => <MobileRecordCardSkeleton key={i} />)}
            </div>
          )
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell align="left">{i18n.t("tags.table.name")}</TableCell>
                <TableCell align="center">Kanban</TableCell>
                <TableCell align="center">
                  {i18n.t("tags.table.tickets")}
                </TableCell>
                <TableCell align="center">
                  {i18n.t("tags.table.actions")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tags.map((tag) => (
                <TableRow key={tag.id} hover>
                  <TableCell align="left">
                    <Chip
                      className={classes.tagChip}
                      style={{ backgroundColor: tag.color }}
                      label={tag.name}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    {tag.kanban === 1 ? (
                      <Tooltip title="Habilitada no Kanban">
                        <Chip
                          className={classes.kanbanChip}
                          icon={<ViewWeekIcon style={{ fontSize: 12, color: "#fff" }} />}
                          label="Kanban"
                          size="small"
                        />
                      </Tooltip>
                    ) : (
                      <Typography variant="caption" color="textDisabled">—</Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">{tag.ticketsCount}</TableCell>
                  <TableCell align="center">{renderActions(tag)}</TableCell>
                </TableRow>
              ))}
              {loading && <TableRowSkeleton columns={4} />}
            </TableBody>
          </Table>
        )}
      </Paper>
    </MainContainer>
  );
};

export default Tags;
